import Dialog from "../Shared/Dialog";
import { useEffect, useState } from "react";
import { useWallet } from "../../app/hooks/wallet/useWallet";
import { useWebEvents } from "../../app/hooks/webevents/useWebEvents";
import { ITopUpListener } from "../../app/hooks/webevents/listeners/ITopUpListener";
import { ITopUpEvent } from "../../app/hooks/webevents/models/ITopUpEvent";
import { toFormattedPrice } from "../../app/services/format";
import { Theme, makeStyles } from "@material-ui/core";
import { IColor, useDeGrazieTheme } from "../../app/hooks/theme/useDeGrazieTheme";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../app/hooks";

interface StyleProps {
    primarycolor: IColor;
}

const useMasterCardStyles = makeStyles<Theme, StyleProps>(() => {
    const shadowOverhead = 40;
    const mastercardPhotoSizeHeight = 190;
    const mastercardPhotoSizeWidth = 256;

    return ({
        photo: {
            top: `${shadowOverhead}px`, //Overhead due to shadow
            left: 0,
            right: 0,
            width: `${mastercardPhotoSizeWidth}px`,
            filter: "drop-shadow(0px 4px 24px gray)",
            height: `${mastercardPhotoSizeHeight}px`,
            margin: "0 auto",
            zIndex: 999999,
            position: "absolute",
            borderRadius: "16px",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
        },
        photoOverheadDiv: {
            height: `${mastercardPhotoSizeHeight/2 + shadowOverhead}px`,
        },
        dialogContainer: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
        },
        contentContainer: {
            borderRadius: "15px 15px 0 0",
            backgroundColor: "#7F7F7F",
            margin: "0 auto",
            padding: "40px",
            position: "relative",
            paddingTop: "90px",
            color: "#FFFFFF",
        },
        nameinfo: {
            textAlign: "center",
        },
        title: {
            overflowY: "auto",
            maxHeight: "20vh",
            color: "#FFFFFF",

            fontFamily: 'Poppins',
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "18px"
        },
        balanceTxt: {
            fontWeight: 500,
            color: "#FFFFFF",

            fontFamily: 'Poppins',
            fontStyle: "normal",
            lineHeight: "54px",
            textAlign: "center",
            fontSize: "36px",
        },
        description: {
            padding: "8px",
            color: "#CACACA",

            fontFamily: 'Poppins',
            fontStyle: "normal",
            fontWeight: 300,
            textAlign: "center",
            fontSize: "12px",
            lineHeight: "18px",
        },
    })
});

const useStyles = makeStyles<Theme, StyleProps>((p) => {
    const shadowOverhead = 40;
    const photoSize = 150;
    return ({
        photo: {
            top: `${shadowOverhead}px`, //Overhead due to shadow
            left: 0,
            right: 0,
            width: `${photoSize}px`,
            filter: "drop-shadow(0px 4px 24px gray)",
            height: `${photoSize}px`,
            margin: "0 auto",
            zIndex: 999999,
            position: "absolute",
            borderRadius: "16px",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
        },
        photoOverheadDiv: {
            height: `${photoSize/2 + shadowOverhead}px`,
        },
        dialogContainer: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
        },
        contentContainer: {
            borderRadius: "15px 15px 0 0",
            backgroundColor: "white",
            margin: "0 auto",
            padding: "40px",
            position: "relative",
            paddingTop: "90px",
        },
        nameinfo: {
            textAlign: "center",
        },
        description: {
            overflowY: "auto",
            maxHeight: "20vh",
        },
        divider: {
            borderBottom: "1px solid #DFE0DF",
            width: "35%",
            margin: "1.5rem auto",
        },
        priceInfo: {
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-evenly"
        },
        priceDescription: {
            padding: "8px",
            fontSize: "1.5rem",
            fontWeight: 100,
        },
        priceTxt: {
            padding: "8px",
            fontSize: "1.5rem",
            fontWeight: 500,
        },
    })
});

const WalletTopUpDialog = () => {
    const wallet = useWallet();

    const isAuth = useAppSelector(state => state.user.isAuth);
    const { t } = useTranslation();
    const [webClient] = useWebEvents();
    const theme = useDeGrazieTheme();
    const classes = useStyles({ primarycolor: theme.primaryColor });
    const mastercardClasses = useMasterCardStyles({ primarycolor: theme.primaryColor });

    const topUpListener: ITopUpListener = {
        onTopUp: (e: ITopUpEvent) => {
            setIsMastercard(e.isMasterCardCampaign == true);
            setIsOpen(true);
            setTopUpAmount(e.amount);
        },
    }

    const [isOpen, setIsOpen] = useState(false);
    const [topUpAmount, setTopUpAmount] = useState(0);
    const [isMastercard, setIsMastercard] = useState(false);
    
    useEffect(() => {
        if(isAuth == false) {
            return;
        }
        
        webClient.addTopUpListener(topUpListener);
        return () => webClient.removeTopUpListener(topUpListener);
    }, [webClient, topUpListener]);

    if(isMastercard) {
        return (
            <Dialog isOpen={isOpen} onClose={() => setIsOpen(false)} style={{backgroundColor: "transparent"}}>
                <div style={{position: "relative"}}>
                    <div
                        className={mastercardClasses.photo}
                        style={{
                            backgroundImage: `url('data:image/svg+xml,<svg width="256" height="190" viewBox="0 0 256 190" fill="none" xmlns="http://www.w3.org/2000/svg"><g filter="url(%23filter0_d_12_425)"><g clip-path="url(%23clip0_12_425)"><g filter="url(%23filter1_b_12_425)"><rect x="18.9458" y="37.1307" width="203" height="128.129" rx="15" transform="rotate(-6.52225 18.9458 37.1307)" fill="url(%23paint0_linear_12_425)"/><rect x="19.2226" y="37.3507" width="202.5" height="127.629" rx="14.75" transform="rotate(-6.52225 19.2226 37.3507)" stroke="url(%23paint1_linear_12_425)" stroke-width="0.5"/></g><path d="M344.183 131.502C350.29 184.916 311.92 233.17 258.481 239.279C205.042 245.389 156.77 207.041 150.664 153.627C144.557 100.213 182.927 51.9594 236.366 45.8497C289.805 39.7401 338.077 78.088 344.183 131.502Z" stroke="white" stroke-width="0.5"/></g></g><path d="M209.15 113.029L198.828 114.209L200.948 132.749L211.27 131.569L209.15 113.029Z" fill="%23FF5F00"/><path d="M200.544 123.405C200.339 121.619 200.542 119.81 201.139 118.115C201.736 116.42 202.711 114.882 203.99 113.619C202.094 112.451 199.906 111.84 197.678 111.855C195.451 111.871 193.272 112.512 191.392 113.707C189.511 114.901 188.005 116.601 187.045 118.61C186.085 120.62 185.71 122.859 185.963 125.072C186.216 127.284 187.087 129.381 188.476 131.122C189.864 132.863 191.715 134.178 193.817 134.918C195.918 135.657 198.185 135.79 200.359 135.303C202.533 134.815 204.526 133.726 206.11 132.16C204.578 131.218 203.282 129.94 202.317 128.423C201.353 126.907 200.747 125.19 200.544 123.405Z" fill="%23EB001B"/><path d="M224.135 120.707C224.388 122.92 224.013 125.159 223.053 127.168C222.093 129.178 220.587 130.877 218.707 132.072C216.826 133.266 214.648 133.908 212.42 133.924C210.192 133.939 208.005 133.328 206.108 132.16C207.386 130.896 208.36 129.358 208.957 127.663C209.554 125.968 209.758 124.16 209.554 122.374C209.35 120.589 208.743 118.873 207.779 117.357C206.815 115.84 205.519 114.562 203.989 113.619C205.573 112.053 207.565 110.964 209.739 110.476C211.913 109.988 214.18 110.122 216.282 110.861C218.383 111.6 220.234 112.916 221.622 114.657C223.011 116.398 223.882 118.495 224.135 120.707Z" fill="%23F79E1B"/><path d="M223.843 128.141L223.8 127.762L223.953 127.744L223.944 127.667L223.554 127.712L223.563 127.789L223.716 127.771L223.76 128.151L223.843 128.141ZM224.6 128.055L224.548 127.597L224.429 127.611L224.327 127.941L224.154 127.642L224.034 127.656L224.086 128.114L224.171 128.104L224.131 127.759L224.294 128.042L224.382 128.032L224.477 127.719L224.516 128.064L224.6 128.055Z" fill="%23F79E1B"/><defs><filter id="filter0_d_12_425" x="-1.0542" y="-0.927856" width="256.24" height="190.359" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/><feOffset dy="5"/><feGaussianBlur stdDeviation="10"/><feComposite in2="hardAlpha" operator="out"/><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0"/><feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_12_425"/><feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_12_425" result="shape"/></filter><filter id="filter1_b_12_425" x="5.55127" y="0.677521" width="243.03" height="177.148" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feGaussianBlur in="BackgroundImageFix" stdDeviation="7.5"/><feComposite in2="SourceAlpha" operator="in" result="effect1_backgroundBlur_12_425"/><feBlend mode="normal" in="SourceGraphic" in2="effect1_backgroundBlur_12_425" result="shape"/></filter><linearGradient id="paint0_linear_12_425" x1="54.1043" y1="46.5228" x2="186.198" y2="165.321" gradientUnits="userSpaceOnUse"><stop stop-opacity="0.8"/><stop offset="1" stop-opacity="0.5"/></linearGradient><linearGradient id="paint1_linear_12_425" x1="24.8677" y1="43.0161" x2="221.895" y2="165.342" gradientUnits="userSpaceOnUse"><stop stop-opacity="0.5"/><stop offset="1" stop-color="%23FF3F01" stop-opacity="0.5"/></linearGradient><clipPath id="clip0_12_425"><rect x="18.9458" y="37.1307" width="203" height="128.129" rx="15" transform="rotate(-6.52225 18.9458 37.1307)" fill="white"/></clipPath></defs></svg>')`,                       
                        }}
                    />
                    <div className={mastercardClasses.dialogContainer}>
                        <div className={mastercardClasses.photoOverheadDiv} onClick={() => setIsOpen(false)}/>
                        <div className={mastercardClasses.contentContainer}>
                            <div className="container">
                                <div className={mastercardClasses.nameinfo}>
                                    <p className={mastercardClasses.title}>{t("mastercardCampaign.topup.title")}</p>
                                    <p className={mastercardClasses.balanceTxt}>{toFormattedPrice(topUpAmount, "€")}</p>
                                    <p className={mastercardClasses.description}>{t("mastercardCampaign.topup.description")}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        )
    }

    return (
        <Dialog isOpen={isOpen} onClose={() => setIsOpen(false)} style={{backgroundColor: "transparent"}}>
            <div style={{position: "relative"}}>
                <div
                    className={classes.photo}
                    style={{
                        backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" id="Artwork" viewBox="0 0 128 128"><style xmlns="" class="adjust-screen-brightness" media="screen">html::before { content: " "; z-index: 2147483647; pointer-events: none; position: fixed; left: 0; top: 0; width: 100vw; height: 100vh; background-color: rgba(0, 0, 0, 0.19999999999999996);}</style><defs><style> .cls-1 { fill: none; } .cls-2 { clip-path: url(%23clip-path); } .cls-3 { fill: %23fbdacd; } .cls-4 { fill: %23260046; } .cls-5 { fill: %23eaeaea; } .cls-6 { fill: %23ea4403; } .cls-7 { fill: %23fff; } </style><clipPath id="clip-path" transform="translate(0 0)"><rect class="cls-1" width="128" height="128"/></clipPath></defs><g class="cls-2"><path class="cls-3" d="M64,128A64,64,0,1,0,0,64a64,64,0,0,0,64,64" transform="translate(0 0)"/><path class="cls-4" d="M35,68a9.91,9.91,0,0,1-4.63-1.21A9.23,9.23,0,0,1,25.8,68a7.66,7.66,0,0,1-6-3.07,2,2,0,0,1,3.25-2.32A3.63,3.63,0,0,0,25.8,64a6.27,6.27,0,0,0,1.48-.22,7.25,7.25,0,0,1-.91-3.62c0-3.94,1.95-5.7,3.89-5.7a3.52,3.52,0,0,1,3,1.93,7.46,7.46,0,0,1,.85,3.77,7.86,7.86,0,0,1-.79,3.54A5.59,5.59,0,0,0,35,64c1.75,0,2.26-.92,2.26-.93A2,2,0,1,1,41,64.68,6.25,6.25,0,0,1,35,68" transform="translate(0 0)"/><path class="cls-5" d="M32.37,60.2c0,2.06-.93,3.73-2.07,3.73s-2.07-1.67-2.07-3.73.93-3.72,2.07-3.72,2.07,1.67,2.07,3.72" transform="translate(0 0)"/><path class="cls-4" d="M30.3,65.69c-2.18,0-3.82-2.36-3.82-5.49s1.64-5.48,3.82-5.48,3.83,2.36,3.83,5.48-1.65,5.49-3.83,5.49m0-7.12a4.47,4.47,0,0,0,0,3.27,4.47,4.47,0,0,0,0-3.27" transform="translate(0 0)"/><path class="cls-6" d="M103.76,57.32l-.17-.07C98.39,55.17,95,51.66,91.82,48V33.62a.81.81,0,0,0-.76-.81l-.27,0c-4.2.42-8.21,3.3-10.26,5-3.54-1.91-8-3.14-14.07-3.14-18.92,0-29.79,11.72-29.79,27.17,0,9.51,4.43,17.17,12.08,21.72v9.93a1.67,1.67,0,0,0,1.67,1.67h9.4a1.68,1.68,0,0,0,1.68-1.67V87.83a44.62,44.62,0,0,0,5.89.38c1,0,2.06,0,3-.1v5.41a1.67,1.67,0,0,0,1.67,1.67H81.5a1.67,1.67,0,0,0,1.67-1.67V84.79c9-4.53,13.1-12.26,20.34-14.82a.86.86,0,0,0,.7-.81V58a.81.81,0,0,0-.45-.72" transform="translate(0 0)"/><path class="cls-7" d="M84.8,55.86a2.69,2.69,0,1,1,2.69,2.69,2.68,2.68,0,0,1-2.69-2.69" transform="translate(0 0)"/><path class="cls-7" d="M77.29,46.74a1.92,1.92,0,0,1-1-.28A21.94,21.94,0,0,0,66.22,44a20.93,20.93,0,0,0-9.93,2.47,2,2,0,0,1-2-3.35,24.9,24.9,0,0,1,12-3,25.44,25.44,0,0,1,12.07,3,2,2,0,0,1-1,3.65" transform="translate(0 0)"/><path class="cls-7" d="M66.68,55.12a1,1,0,0,1,1,1v.53A8.71,8.71,0,0,1,71.45,58a1.29,1.29,0,0,1,.73,1.16,1.33,1.33,0,0,1-1.35,1.33,1.34,1.34,0,0,1-.72-.22,9,9,0,0,0-2.49-1.06v4.63c3.66.92,5.23,2.39,5.23,5s-2.07,4.41-5.13,4.7V75a1,1,0,0,1-1,1,1,1,0,0,1-1.06-1v-1.5a10.42,10.42,0,0,1-5-1.88A1.33,1.33,0,0,1,60,70.44a1.34,1.34,0,0,1,2.13-1.07A8.38,8.38,0,0,0,65.72,71V66.24c-3.53-.92-5.17-2.24-5.17-5s2.05-4.37,5.07-4.61v-.5a1,1,0,0,1,1.06-1m-1,8.22V59.08c-1.55.14-2.32,1-2.32,2s.46,1.64,2.32,2.24m1.9,3.36v4.39c1.54-.17,2.39-1,2.39-2.1s-.53-1.72-2.39-2.29" transform="translate(0 0)"/></g></svg>')`,
                    }}
                />
                <div className={classes.dialogContainer}>
                    <div className={classes.photoOverheadDiv} onClick={() => setIsOpen(false)}/>
                    <div className={classes.contentContainer}>
                        <div className="container">
                            <div className={classes.nameinfo}>
                                <p className={classes.priceTxt}>+ {toFormattedPrice(topUpAmount, "€")}</p>
                                <p className={classes.description}>{t("walletTopUp.dialog.topUpDescription")}</p>
                            </div>
                            <div className={classes.divider}></div>
                            <div className={classes.priceInfo}>
                                <span className={classes.priceDescription}>{t("walletTopUp.dialog.yourBalance")}</span>
                                <span className={classes.priceTxt}>{toFormattedPrice(wallet.balance, "€")}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}
export default WalletTopUpDialog;